import { useEffect, useMemo } from 'react';
import { DoubleSide, EdgesGeometry, Matrix4, Quaternion, Vector3 } from 'three';

import { PartPlacement } from 'api/models';
import { ArticleEdgeLines } from 'app/modules/wallforming/components/scenes/objects/articles/ArticleEdgeLines';
import {
    calculateFullQuaternion,
    normalizeMeshName
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';

import type { ArticleMesh } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleMesh';

interface PreviewPartMeshProps {
    articleMeshes: ArticleMesh[];
    part: PartPlacement;
    useDefaultMaterial: boolean;
}

const normal = new Vector3(0, 0, 0);

export const PreviewPartMesh = ({ articleMeshes, part, useDefaultMaterial }: PreviewPartMeshProps) => {
    const quaternion = calculateFullQuaternion(
        normal.set(part.alignment.x, part.alignment.y, part.alignment.z),
        (part.rotation * Math.PI) / 180
    );

    const edgesGeometry = useMemo(() => {
        if (articleMeshes.length === 0) {
            return undefined;
        }

        const transformedGeometry = articleMeshes[0].geometry.clone();
        transformedGeometry.applyMatrix4(
            new Matrix4()
                .makeScale(part.scale.x, part.scale.y, part.scale.z)
                .premultiply(new Matrix4().makeRotationFromQuaternion(quaternion as Quaternion))
        );
        transformedGeometry.translate(part.position.x, part.position.y, part.position.z);

        return new EdgesGeometry(transformedGeometry);
    }, [
        articleMeshes,
        part.position.x,
        part.position.y,
        part.position.z,
        part.scale.x,
        part.scale.y,
        part.scale.z,
        quaternion
    ]);

    useEffect(() => {
        return () => {
            edgesGeometry?.dispose();
        };
    }, [edgesGeometry]);

    return (
        <>
            <group
                position={[part.position.x, part.position.y, part.position.z]}
                quaternion={quaternion}
                scale={[part.scale.x, part.scale.y, part.scale.z]}
            >
                {articleMeshes.map((mesh) => {
                    const normalizedName = normalizeMeshName(mesh.name).toLowerCase();
                    return useDefaultMaterial || normalizedName === 'holes' ? (
                        <mesh key={mesh.id} geometry={mesh.geometry} material={mesh.material} />
                    ) : (
                        <mesh key={mesh.id} geometry={mesh.geometry}>
                            <meshPhysicalMaterial
                                color="#bfd7f0"
                                opacity={0.3}
                                polygonOffsetFactor={-1}
                                polygonOffsetUnits={-4}
                                side={DoubleSide}
                                polygonOffset
                                transparent
                            />
                        </mesh>
                    );
                })}
            </group>

            {edgesGeometry && <ArticleEdgeLines edgeLineThickness="medium" edgesGeometry={edgesGeometry} />}
        </>
    );
};
